// Kiosk mode — designed for a tablet at the venue entrance.
// Before event start  → countdown screen with anticipation messages.
// After event start    → redirects to /register?kiosk=1 so guests can sign in
//                        one after another. The Reveal page detects ?kiosk=1
//                        and automatically returns the kiosk to a "Thank You"
//                        screen for 10 seconds, then back to the form.
//
// Usage: place this URL on the venue device: <app>/kiosk?thanks=1 will land
// here after a guest completes registration.

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Crown, ArrowRight, Sparkles as SparklesIcon, Heart, Check } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { useLang } from "../lib/i18n";
import { getEventConfig } from "../lib/api";
import { formatEventDate, formatEventTime } from "../lib/director";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

const TEN_SECS = 10;

export default function Kiosk() {
  const lang = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const showThanks = params.get("thanks") === "1";
  const guestName = params.get("name") || "";

  const [startIso, setStartIso] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [thanksLeft, setThanksLeft] = useState(TEN_SECS);

  // Load config + tick
  useEffect(() => {
    getEventConfig().then((c) => setStartIso(c.start)).catch(() => {});
    const tm = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tm);
  }, []);

  const startMs = startIso ? new Date(startIso).getTime() : null;
  const started = startMs !== null && now >= startMs;

  // Auto-cycle: after a guest completes, show Thanks for 10 sec, then back to form
  useEffect(() => {
    if (!showThanks) { setThanksLeft(TEN_SECS); return; }
    setThanksLeft(TEN_SECS);
    const tickTm = setInterval(() => {
      setThanksLeft((s) => Math.max(0, s - 1));
    }, 1000);
    const goTm = setTimeout(() => {
      setParams({}, { replace: true });
      // Re-launch the registration form
      navigate("/register?kiosk=1", { replace: true });
    }, TEN_SECS * 1000);
    return () => { clearInterval(tickTm); clearTimeout(goTm); };
  }, [showThanks, navigate, setParams]);

  // Auto-open form when event time arrives (and we're not in thanks state)
  useEffect(() => {
    if (started && !showThanks) {
      navigate("/register?kiosk=1", { replace: true });
    }
  }, [started, showThanks, navigate]);

  // --- Render: Thank-you screen ---
  if (showThanks) {
    return (
      <div
        className="fixed inset-0 grid place-items-center text-blush overflow-hidden"
        style={{ background: "radial-gradient(circle at 50% 0%, #2c0e4f 0%, #160828 60%, #08041a 100%)" }}
        data-testid="kiosk-thanks"
      >
        <Sparkles count={90} />
        <div className="relative z-10 text-center px-6 max-w-3xl animate-[fadeUp_1s_ease-out]">
          <div
            className="mx-auto mb-5 rounded-full grid place-items-center"
            style={{
              width: 100, height: 100,
              background: "linear-gradient(135deg, #D4AF37 0%, #FFE89C 50%, #B8860B 100%)",
              boxShadow: "0 0 50px rgba(252,246,186,0.55)",
            }}
          >
            <Check size={56} className="text-[#3c0d3a]" strokeWidth={3} />
          </div>
          <p className="label-eyebrow tracking-[0.32em]" style={{ ...TE, color: "#D4AF37" }}>
            {lang === "te" ? "✦ రాజ్యానికి స్వాగతం ✦" : "✦ WELCOME TO THE KINGDOM ✦"}
          </p>
          <h1
            className="gold-text-soft font-script drop-shadow-2xl mt-3 leading-tight"
            style={{ ...TE, fontSize: "clamp(34px, 6vw, 64px)" }}
          >
            {lang === "te"
              ? (guestName ? `ధన్యవాదాలు, ${guestName}!` : "ధన్యవాదాలు!")
              : (guestName ? `Thank You, ${guestName}!` : "Thank You!")}
          </h1>
          <p
            className="font-display italic text-blush/90 mt-4 leading-relaxed"
            style={{ ...TE, fontSize: "clamp(15px, 2.6vw, 22px)" }}
          >
            {lang === "te"
              ? "మీరు ఇప్పుడు అనఘ రాజ్యంలోని నక్షత్రం. ప్రొజెక్టర్ తెరపై మీ ముఖంతో మెరుస్తున్నారు ✨"
              : "You are now a star in Anagha's Kingdom. Look at the projector — your face is shining there! ✨"}
          </p>

          <div
            className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(212,175,55,0.45)",
            }}
          >
            <Heart size={14} className="text-[#FFC4D6]" fill="#FFC4D6" />
            <p className="label-eyebrow text-blush/80 tracking-wider" style={TE}>
              {lang === "te"
                ? `తదుపరి అతిథి కోసం ${thanksLeft} సెకన్లలో సిద్ధం`
                : `Ready for the next guest in ${thanksLeft}s`}
            </p>
            <SparklesIcon size={14} className="text-[#FCF6BA]" />
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Pre-event countdown ---
  const diff = startMs ? Math.max(0, startMs - now) : 0;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const labels = lang === "te"
    ? { d: "రోజులు", h: "గంటలు", m: "నిమిషాలు", s: "క్షణాలు" }
    : { d: "Days", h: "Hours", m: "Minutes", s: "Seconds" };

  return (
    <div
      className="fixed inset-0 grid place-items-center text-blush overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 0%, #2c0e4f 0%, #160828 60%, #08041a 100%)" }}
      data-testid="kiosk-pre"
    >
      <div className="absolute top-3 right-3 z-20"><LangToggle light /></div>
      <Sparkles count={70} />

      <div className="relative z-10 text-center px-6 max-w-3xl">
        <Crown className="text-[#FCF6BA] drop-shadow-2xl mx-auto mb-4" size={54} />
        <p className="label-eyebrow tracking-[0.32em]" style={{ ...TE, color: "#D4AF37" }}>
          {lang === "te" ? "✦ స్వాగత కేంద్రం ✦" : "✦ WELCOME KIOSK ✦"}
        </p>
        <h1
          className="gold-text-soft font-script drop-shadow-2xl mt-2 leading-tight"
          style={{ ...TE, fontSize: "clamp(36px, 7vw, 72px)" }}
        >
          {lang === "te" ? "అనఘ రాజ్యం" : "Anagha's Kingdom"}
        </h1>
        <p
          className="font-display italic text-blush/90 mt-3"
          style={{ ...TE, fontSize: "clamp(14px, 2.4vw, 20px)" }}
        >
          {lang === "te"
            ? "ఈవెంట్ ప్రారంభమైన వెంటనే రిజిస్ట్రేషన్ ఫారమ్ తెరుచుకుంటుంది"
            : "The registration form will open automatically when the event begins"}
        </p>

        {startIso && (
          <div
            className="rounded-full px-5 py-2 mt-6 inline-flex items-center gap-2"
            style={{
              background: "rgba(11, 7, 38, 0.85)",
              border: "1.5px solid rgba(212,175,55,0.6)",
              boxShadow: "0 12px 26px rgba(0,0,0,0.45), inset 0 0 18px rgba(212,175,55,0.18)",
              backdropFilter: "blur(8px)",
            }}
          >
            <SparklesIcon size={13} className="text-[#FCF6BA]" />
            <p className="font-script gold-text-soft" style={{ ...TE, fontSize: "clamp(15px, 2.2vw, 20px)" }}>
              {formatEventDate(startIso, lang)} · {formatEventTime(startIso, lang)}
            </p>
            <SparklesIcon size={13} className="text-[#FCF6BA]" />
          </div>
        )}

        <div className="mt-8 flex items-end justify-center gap-4 sm:gap-7 text-blush/95">
          {[[d, labels.d], [h, labels.h], [m, labels.m], [s, labels.s]].map(([val, label], i) => (
            <div key={i} className="flex flex-col items-center">
              <span
                className="gold-text-soft font-display font-semibold leading-none tabular-nums"
                style={{ fontSize: "clamp(36px, 6vw, 80px)" }}
              >
                {String(val).padStart(2, "0")}
              </span>
              <span className="label-eyebrow mt-2 text-blush/80" style={TE}>{label}</span>
            </div>
          ))}
        </div>

        {/* Manual override — host can start early */}
        <Link
          to="/register?kiosk=1"
          className="mt-10 inline-flex items-center gap-2 rounded-full py-3 px-6 transition-transform hover:scale-[1.02]"
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(212,175,55,0.55)",
            color: "#FFE5C7",
            ...TE,
          }}
          data-testid="kiosk-manual-start"
        >
          <SparklesIcon size={14} className="text-[#D4AF37]" />
          {lang === "te" ? "ఇప్పుడే ప్రారంభించండి" : "Start Now"}
          <ArrowRight size={14} />
        </Link>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
